import { getFragmentData } from '@e-socna/data-layer';
import { isDefined } from '@e-socna/utils';

import { formatDate } from '../../../utils/format-date.util';
import { AddressCardCmp_Address_Fragment } from '../../Addresses/Card';
import { SearchTolerance } from '../../FilterableCards/utils/search-tolerance.util';

import { FolderBusinessEntityListCmp_ThirdParty_Fragment } from '../fragments/third-party.fragment';
import type { FolderBusinessEntityListCmp_FolderBusinessEntity } from '../fragments/folder-business-entity.fragment';

// An entity of the list, its state joined from the references.
export type SearchableEntity =
  FolderBusinessEntityListCmp_FolderBusinessEntity &
    Readonly<{ state: Readonly<{ label: string }> }>;

// A search criterion: the texts of an entity it searches in (mutable, as match-sorter types
// its keys), and what it is to the user.
export interface SearchCriterion {
  readonly key: (entity: SearchableEntity) => string[];
  readonly explanation: string;
  readonly tolerance: SearchTolerance;
}

type Actors = readonly Readonly<{
  thirdParty: FolderBusinessEntityListCmp_FolderBusinessEntity['consolidation']['quotationReceiverActors'][number]['thirdParty'];
}>[];

const presentTexts = (
  texts: readonly (string | null | undefined)[],
): string[] => texts.filter(isDefined).filter(Boolean);

// The names of the third parties of actors: raison sociale and nom commercial of an
// organization, full and last names of a person.
const thirdPartyNamesOf = (actors: Actors): string[] =>
  actors.flatMap(({ thirdParty }) => {
    const party = getFragmentData(
      FolderBusinessEntityListCmp_ThirdParty_Fragment,
      thirdParty,
    );

    return presentTexts(
      'name' in party
        ? [party.name, party.tradingName]
        : [party.fullName, party.lastName],
    );
  });

const postalInfoOf = (entity: SearchableEntity) =>
  getFragmentData(AddressCardCmp_Address_Fragment, entity.consolidation.address)
    .postalInfo;

const codeOf = (entity: SearchableEntity): string[] =>
  'code' in entity ? [entity.code] : [];

const commonCriteriaOf = (entityLabel: string): readonly SearchCriterion[] => [
  {
    // The quotation receivers among them.
    key: ({ consolidation }) => thirdPartyNamesOf(consolidation.externalActors),
    explanation: 'Noms des acteurs externes',
    tolerance: SearchTolerance.Approximate,
  },
  {
    key: ({ consolidation }) =>
      thirdPartyNamesOf(consolidation.quotationEmitterActors),
    explanation: 'Noms des émetteurs du devis',
    tolerance: SearchTolerance.Approximate,
  },
  {
    key: ({ consolidation }) => presentTexts([consolidation.title]),
    explanation: `Titre ${entityLabel}`,
    tolerance: SearchTolerance.Near,
  },
  {
    key: (entity) => presentTexts([postalInfoOf(entity)?.postalCode]),
    explanation: `Code postal de l'adresse ${entityLabel}`,
    tolerance: SearchTolerance.Exact,
  },
  {
    key: (entity) => {
      const postalInfo = postalInfoOf(entity);

      return presentTexts([postalInfo?.route, postalInfo?.locality]);
    },
    explanation: `Rue et commune de l'adresse ${entityLabel}`,
    tolerance: SearchTolerance.Near,
  },
  {
    key: ({ state }) => [state.label],
    explanation: `État ${entityLabel}`,
    tolerance: SearchTolerance.Near,
  },
  {
    key: ({ businessCreationDate }) => [
      formatDate(businessCreationDate, 'dd/MM/yyyy'),
    ],
    explanation: `Date de création ${entityLabel} (jj/mm/aaaa)`,
    tolerance: SearchTolerance.Exact,
  },
  {
    key: ({ consolidation }) => presentTexts([consolidation.description]),
    explanation: `Description ${entityLabel}`,
    tolerance: SearchTolerance.Near,
  },
  {
    key: ({ consolidation }) =>
      consolidation.externalActors.flatMap((actor) =>
        'references' in actor ? actor.references.map(({ value }) => value) : [],
      ),
    explanation: 'Références données par les acteurs externes',
    tolerance: SearchTolerance.Exact,
  },
];

export const FOLDER_SEARCH_CRITERIA: readonly SearchCriterion[] = [
  {
    key: codeOf,
    explanation: 'Code du dossier',
    tolerance: SearchTolerance.Exact,
  },
  ...commonCriteriaOf('du dossier'),
];

export const MISSION_SEARCH_CRITERIA: readonly SearchCriterion[] = [
  {
    key: codeOf,
    explanation: 'Code de la mission',
    tolerance: SearchTolerance.Exact,
  },
  ...commonCriteriaOf('de la mission'),
  {
    key: (entity) =>
      entity.__typename === 'Mission' && entity.type ? [entity.type.code] : [],
    explanation: 'Code du type de mission',
    tolerance: SearchTolerance.Exact,
  },
  {
    key: (entity) =>
      entity.__typename === 'Mission' && entity.type ? [entity.type.label] : [],
    explanation: 'Libellé du type de mission',
    tolerance: SearchTolerance.Near,
  },
];

export const BUSINESS_EVENT_SEARCH_CRITERIA: readonly SearchCriterion[] =
  commonCriteriaOf("de l'événement métier");
